import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../services/api'
import { useAuth } from './use-auth'
import type { Notification } from '@alpha-edge/shared'

const POLL_INTERVAL_MS = 60_000

interface UnreadCountResponse {
  count: number
}

interface NotificationListResponse {
  rows: Notification[]
  nextCursor: string | null
}

export function useNotificationUnreadCount(): { count: number; loading: boolean; refresh: () => void } {
  const { user } = useAuth()
  const [count, setCount] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchCount = useCallback(() => {
    api
      .get<UnreadCountResponse>('/notifications/unread-count')
      .then((res) => {
        setCount(res.data?.count ?? 0)
      })
      .catch(() => {
        setCount(0)
      })
  }, [])

  useEffect(() => {
    if (!user) return

    fetchCount()
    timerRef.current = setInterval(fetchCount, POLL_INTERVAL_MS)

    const handleFocus = () => fetchCount()
    window.addEventListener('focus', handleFocus)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      window.removeEventListener('focus', handleFocus)
    }
  }, [user, fetchCount])

  if (!user) return { count: 0, loading: false, refresh: () => {} }

  return { count, loading: false, refresh: fetchCount }
}

export function useNotificationList(opts?: { onlyUnread?: boolean }): {
  rows: Notification[]
  unreadCount: number
  loading: boolean
  loadMore: () => Promise<void>
  hasMore: boolean
  markRead: (ids: string[]) => Promise<void>
  markAllRead: () => Promise<void>
  refresh: () => Promise<void>
} {
  const [rows, setRows] = useState<Notification[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchInitial = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '20' })
      if (opts?.onlyUnread) params.set('unread', '1')
      const res = await api.get<NotificationListResponse>(`/notifications?${params.toString()}`)
      setRows(res.data?.rows ?? [])
      setNextCursor(res.data?.nextCursor ?? null)
    } catch {
      setRows([])
      setNextCursor(null)
    } finally {
      setLoading(false)
    }
  }, [opts?.onlyUnread])

  useEffect(() => {
    void fetchInitial()
  }, [fetchInitial])

  const loadMore = useCallback(async () => {
    if (!nextCursor) return
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '20', cursor: nextCursor })
      if (opts?.onlyUnread) params.set('unread', '1')
      const res = await api.get<NotificationListResponse>(`/notifications?${params.toString()}`)
      setRows((prev) => [...prev, ...(res.data?.rows ?? [])])
      setNextCursor(res.data?.nextCursor ?? null)
    } catch {
      // no-op on loadMore failure
    } finally {
      setLoading(false)
    }
  }, [nextCursor, opts?.onlyUnread])

  const markRead = useCallback(
    async (ids: string[]) => {
      await api.post('/notifications/mark-read', { ids })
      await fetchInitial()
    },
    [fetchInitial],
  )

  const markAllRead = useCallback(async () => {
    await api.post('/notifications/mark-all-read', {})
    await fetchInitial()
  }, [fetchInitial])

  const unreadCount = rows.filter((r) => r.readAt === null).length

  return {
    rows,
    unreadCount,
    loading,
    loadMore,
    hasMore: nextCursor !== null,
    markRead,
    markAllRead,
    refresh: fetchInitial,
  }
}
